/**
 * Filter toolbar shared by the Clients list and the Customer 360 search:
 * status (with counts) · group on the left, sort · date range · reset on the
 * right. Driven entirely by a `useClients()` result, so both pages keep the
 * same URL contract (?status / ?locked / ?group / ?from / ?to / ?sortBy).
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { Calendar, Check, CheckCircle2, ChevronDown, Lock, Users, X, XCircle } from 'lucide-react'
import type { useClients, ClientSortBy, ClientSortDir } from '../../../hooks/useClients.js'
import Select from '../../ui/Select.js'
import DatePicker from '../../ui/DatePicker.js'

/* ── Design tokens: radius capped at 3px across the whole surface ── */
const R = 'rounded-[3px]'
const CONTROL = `flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`
const CONTROL_ON = 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]'
const MENU = `absolute top-full z-40 mt-1.5 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/5`
const MENU_ITEM = 'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium transition-colors'

/* ── Status filter ──
   Active / Inactive / Locked live behind one control; the mapping back to the
   hook's separate `status` and `locked` params keeps the URL contract unchanged. */
export type ClientStatusFilter = 'all' | 'active' | 'inactive' | 'locked'

const STATUS_FILTERS: { key: ClientStatusFilter; label: string; icon: JSX.Element }[] = [
  { key: 'all', label: 'All Clients', icon: <Users className="h-4 w-4" /> },
  { key: 'active', label: 'Active', icon: <CheckCircle2 className="h-4 w-4" /> },
  { key: 'inactive', label: 'Inactive', icon: <XCircle className="h-4 w-4" /> },
  { key: 'locked', label: 'Locked', icon: <Lock className="h-4 w-4" /> },
]

export function deriveStatusFilter(status: string, locked: string): ClientStatusFilter {
  if (status === 'true' && !locked) return 'active'
  if (status === 'false' && !locked) return 'inactive'
  if (locked === 'true' && !status) return 'locked'
  return 'all'
}

const SORTS: { value: `${ClientSortBy}:${ClientSortDir}`; label: string }[] = [
  { value: 'updated_at:desc', label: 'Recently updated' },
  { value: 'full_name:asc', label: 'Name A–Z' },
  { value: 'full_name:desc', label: 'Name Z–A' },
  { value: 'email:asc', label: 'Email A–Z' },
  { value: 'created_at:desc', label: 'Newest first' },
  { value: 'created_at:asc', label: 'Oldest first' },
]

export default function ClientsFilterToolbar({
  clients: c,
  groups,
}: {
  clients: ReturnType<typeof useClients>
  groups: { id: string; name: string }[]
}): JSX.Element {
  const [showDateFilter, setShowDateFilter] = useState(Boolean(c.dateFrom || c.dateTo))
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [groupMenuOpen, setGroupMenuOpen] = useState(false)
  const statusMenuRef = useRef<HTMLDivElement>(null)
  const groupMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!statusMenuOpen && !groupMenuOpen) return
    const handler = (e: MouseEvent): void => {
      const t = e.target as Node
      if (statusMenuRef.current && !statusMenuRef.current.contains(t)) setStatusMenuOpen(false)
      if (groupMenuRef.current && !groupMenuRef.current.contains(t)) setGroupMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [statusMenuOpen, groupMenuOpen])

  const statusFilter = deriveStatusFilter(c.status, c.locked)
  const statusCounts: Record<ClientStatusFilter, number> = {
    all: c.summary.total, active: c.summary.active, inactive: c.summary.inactive, locked: c.summary.locked,
  }
  const activeStatusMeta = STATUS_FILTERS.find((f) => f.key === statusFilter) ?? STATUS_FILTERS[0]!

  const handleStatusFilterChange = (next: ClientStatusFilter): void => {
    setStatusMenuOpen(false)
    if (next === 'active') { c.setStatus('true'); c.setLocked('') }
    else if (next === 'inactive') { c.setStatus('false'); c.setLocked('') }
    else if (next === 'locked') { c.setLocked('true'); c.setStatus('') }
    else { c.setStatus(''); c.setLocked('') }
  }

  const dateActive = Boolean(c.dateFrom || c.dateTo)
  const selectedGroup = groups.find((g) => g.id === c.groupId) ?? null
  const refinementCount =
    (c.search ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (c.groupId ? 1 : 0) + (dateActive ? 1 : 0)

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-slate-50/70 px-6 py-2.5">
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Status (All / Active / Inactive / Locked) with counts */}
        <div ref={statusMenuRef} className="relative">
          <button onClick={() => setStatusMenuOpen((v) => !v)} className={`${CONTROL} ${statusFilter !== 'all' ? CONTROL_ON : ''}`}>
            <span className="font-normal text-slate-500">Filter:</span>
            <span className={statusFilter !== 'all' ? 'text-[#3366FF]' : 'text-slate-400'}>{activeStatusMeta.icon}</span>
            {activeStatusMeta.label}
            {statusCounts[statusFilter] > 0 && (
              <span className={`font-mono text-[11px] font-bold tabular-nums ${statusFilter !== 'all' ? 'text-[#3366FF]' : 'text-slate-400'}`}>
                ({statusCounts[statusFilter].toLocaleString()})
              </span>
            )}
            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${statusMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {statusMenuOpen && (
            <div className={`${MENU} left-0 w-52`}>
              {STATUS_FILTERS.map((f) => {
                const on = statusFilter === f.key
                const count = statusCounts[f.key]
                return (
                  <button
                    key={f.key}
                    onClick={() => handleStatusFilterChange(f.key)}
                    className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}>{f.icon}</span>
                    <span className="flex-1">{f.label}</span>
                    {count > 0 && (
                      <span className={`text-[11px] font-bold tabular-nums ${on ? 'text-[#3366FF]' : 'text-slate-400'}`}>
                        {count.toLocaleString()}
                      </span>
                    )}
                    {on && <Check className="h-4 w-4" />}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Group */}
        <div ref={groupMenuRef} className="relative">
          <button onClick={() => setGroupMenuOpen((v) => !v)} className={`${CONTROL} ${c.groupId ? CONTROL_ON : ''}`}>
            <span className="font-normal text-slate-500">Group:</span>
            <Users className={`h-4 w-4 ${c.groupId ? 'text-[#3366FF]' : 'text-slate-400'}`} />
            {selectedGroup?.name ?? 'All groups'}
            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${groupMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {groupMenuOpen && (
            <div className={`${MENU} left-0 w-56`}>
              <button
                onClick={() => { c.setGroupId(''); setGroupMenuOpen(false) }}
                className={`${MENU_ITEM} ${!c.groupId ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Users className="h-4 w-4 shrink-0" />
                <span className="flex-1">All groups</span>
                {!c.groupId && <Check className="h-4 w-4" />}
              </button>
              {groups.length > 0 && <div className="my-1 border-t border-slate-100" />}
              <div className="max-h-64 overflow-y-auto">
                {groups.map((g) => {
                  const on = c.groupId === g.id
                  return (
                    <button
                      key={g.id}
                      onClick={() => { c.setGroupId(g.id); setGroupMenuOpen(false) }}
                      className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <span className="flex-1 truncate">{g.name}</span>
                      {on && <Check className="h-4 w-4" />}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {/* Sort */}
        <Select
          ariaLabel="Sort clients"
          size="sm"
          value={`${c.sortBy}:${c.sortDir}`}
          onChange={(v) => {
            const [by, dir] = v.split(':') as [ClientSortBy, ClientSortDir]
            c.setSort(by, dir)
          }}
          options={SORTS.map((s) => ({ value: s.value, label: s.label }))}
          className="w-[150px]"
        />

        {/* Date range */}
        {showDateFilter || dateActive ? (
          <div className="flex items-center gap-1.5">
            <DatePicker
              ariaLabel="From date"
              size="sm"
              label="From"
              value={c.dateFrom}
              onChange={(v) => c.setDateRange(v, c.dateTo)}
            />
            <DatePicker
              ariaLabel="To date"
              size="sm"
              label="To"
              value={c.dateTo}
              onChange={(v) => c.setDateRange(c.dateFrom, v)}
            />
            <button onClick={() => { c.setDateRange('', ''); setShowDateFilter(false) }} className={`${R} p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700`} title="Clear dates">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button onClick={() => setShowDateFilter(true)} className={CONTROL} title="Filter by date">
            <Calendar className="h-4 w-4 text-slate-400" />Date Range
          </button>
        )}

        {refinementCount > 0 && (
          <>
            <div className="mx-1 h-4 w-px bg-slate-300" />
            <button
              onClick={c.clearFilters}
              title={`Clear ${refinementCount} active filter${refinementCount === 1 ? '' : 's'}`}
              className="px-1 text-xs font-medium text-[#3366FF] transition-colors hover:text-[#1d4ed8]"
            >
              Reset filters
            </button>
          </>
        )}
      </div>
    </div>
  )
}
